import { enqueueSnackbar } from 'notistack';
import React, { SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDelete from '../../../../shared/components/ConfirmDelete';
import CustomDataGrid from '../../../../shared/components/CustomDataGrid';
import { ELeaveDaysStatus } from '../../../../shared/enums/leave-days/ELeaveDays';
import { handleError } from '../../../../shared/functions/handleError';
import { useLeaveDaysColumns } from '../../../../shared/hooks/useLeaveDaysColumns';
import { IPagination } from '../../../../shared/interfaces/shared/IPagination';
import { ILeaveDay } from '../../../../shared/interfaces/leave-days/ILeaveDay';
import { AdminLeaveDaysService } from '../service';
import LeaveDaysFormModal from './LeaveDaysFormModal';
import LeaveDaysFormModalBody from './LeaveDaysFormModalBody';

interface IProps {
  handleUpdateList: () => void;
  data: ILeaveDay[] | undefined;
  totalPages: number;
  count: number;
  isAddModalOpened: boolean;
  pagination: IPagination;
  setPagination: React.Dispatch<React.SetStateAction<IPagination>>;
  setIsAddModalOpened: React.Dispatch<SetStateAction<boolean>>;
  selectedLeaveDay: ILeaveDay | null;
  setSelectedLeaveDay: React.Dispatch<SetStateAction<ILeaveDay | null>>;
}
export const LeaveDaysTable = ({
  data,
  count,
  handleUpdateList,

  isAddModalOpened,
  setIsAddModalOpened,
  selectedLeaveDay,
  totalPages,
  pagination,
  setPagination,
  setSelectedLeaveDay,
}: IProps) => {
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const columns = useLeaveDaysColumns(
    setSelectedLeaveDay,
    setShowDeleteModal,
    setIsAddModalOpened,
  );
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedLeaveDay(null);
  };
  const deleteHandler = async (leaveDayId: number) => {
    !!leaveDayId &&
      (await AdminLeaveDaysService.delete(leaveDayId)
        .then((response) => {
          if (response) {
            handleUpdateList();
            enqueueSnackbar(
              t('toasts.successfullyDeleted', {
                title: `ID: ${selectedLeaveDay?.id}`,
              }),
              {
                variant: 'success',
              },
            );
            closeDeleteModal();
          }
        })
        .catch((error) => handleError(error))
        .finally(() => setIsLoading(false)));
  };
  const handleSubmit = async () => {
    setIsLoading(true);
    if (!!selectedLeaveDay?.id) {
      try {
        // Check if the selected leave-day's status is not PENDING
        if (selectedLeaveDay?.status !== ELeaveDaysStatus.PENDING) {
          // Try to update the status
          await AdminLeaveDaysService.updateStatus(
            selectedLeaveDay?.id,
            selectedLeaveDay?.status,
          );

          // Stop execution here if the status was updated
          handleUpdateList();

          enqueueSnackbar(
            t('toasts.successfullyUpdated', {
              title: `ID: ${selectedLeaveDay?.id}`,
            }),
            { variant: 'success' },
          );
          setIsAddModalOpened(false);
          setSelectedLeaveDay(null);
          return; // Prevent further execution
        }

        // Update the leave-day details only if the status update was not needed
        const response = await AdminLeaveDaysService.update(
          selectedLeaveDay?.id,
          selectedLeaveDay,
        );

        // Handle the response if update was successful
        if (response) {
          handleUpdateList();

          enqueueSnackbar(
            t('toasts.successfullyUpdated', {
              title: `ID: ${selectedLeaveDay?.id}`,
            }),
            { variant: 'success' },
          );
          setIsAddModalOpened(false);
          setSelectedLeaveDay(null);
        }
      } catch (error) {
        // Handle any error from updateStatus or update
        handleError(error);
      } finally {
        // Set loading to false at the end, whether success or failure
        setIsLoading(false);
      }
    } else {
      await AdminLeaveDaysService.create({
        ...selectedLeaveDay,
        status: ELeaveDaysStatus.PENDING,
      } as ILeaveDay)
        .then((response) => {
          if (response) {
            handleUpdateList();

            enqueueSnackbar(
              t('toasts.successfullyCreated', { title: `ID: ${response?.id}` }),
              {
                variant: 'success',
              },
            );
            setIsAddModalOpened(false);
            setSelectedLeaveDay(null);
          }
        })
        .catch(handleError)
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <>
      <CustomDataGrid
        hasNextPage={totalPages < pagination?.page}
        rows={data || []}
        columns={columns}
        rowCount={count}
        pagination={pagination}
        setPagination={setPagination}
        loading={data === undefined}
      />
      <LeaveDaysFormModal
        open={isAddModalOpened}
        handleClose={() => setIsAddModalOpened(false)}
        handleSubmit={handleSubmit}
        isLoading={isLoading}
        isUpdate={!!selectedLeaveDay?.id}
      >
        <LeaveDaysFormModalBody
          leaveDay={selectedLeaveDay as ILeaveDay}
          setLeaveDay={setSelectedLeaveDay}
        />
      </LeaveDaysFormModal>
      <ConfirmDelete
        open={showDeleteModal}
        title={`ID: ${selectedLeaveDay?.id}`}
        handleClose={closeDeleteModal}
        handleSubmit={() => deleteHandler(Number(selectedLeaveDay?.id))}
      />
    </>
  );
};
